"use client";

import { SectionHeader } from "@/components/common/section-header";
import { BasicInfo, BasicInfoCta, BasicInfoMultipleCtas } from "@/types/dto/common/basic-info";
import { Box, Stack } from "@mui/system";
import React from "react";
import { deepmerge } from "@mui/utils";
import DynamicCtaButton from "@/components/common/dynamic-cta-button/dynamic-cta-button";
import dynamicCtaButtonListFactory, {dynamicCtaButtonFactory} from "@/factories/common/dynamic-cta-button/dynamic-cta-button-factory";
import {useTranslations} from "next-intl";

interface TextModuleSlotProps {
  header?: {
    sx?: SxObject;
    testId?: string;
  };
  copy?: {
    sx?: SxObject;
    testId?: string;
  };
  container?: {
    sx?: SxObject;
    className?: string;
  };
  root?: {
    sx?: SxObject;
    className?: string;
  };
  ctaContainer?: {
    sx?: SxObject;
    className?: string;
  };
  ctaButton?: {
    sx?: SxObject;
    testId?: string;
  }
}

interface TextModuleProps {
  basicInfo: BasicInfo | BasicInfoMultipleCtas;
  moduleRankingPosition?: number; // Position of the module on the page for tracking purposes
  moduleName?: string;
  moduleParentContainerType?: string;
  slotProps?: TextModuleSlotProps;
  testId?: string;
  hideText?: boolean;
}

const TextModule = ({
  basicInfo,
  moduleParentContainerType,
  moduleName,
  moduleRankingPosition,
  slotProps,
  testId = "text-module",
  hideText = false
}: TextModuleProps) => {
  const { headerText, copyText } = basicInfo;

  const listOfCtas: BasicInfoCta[] | undefined =
    "ctas" in basicInfo && basicInfo.ctas
      ? basicInfo.ctas
      : undefined;

  const singleCtaBasicInfo: BasicInfo | undefined =
    !("ctas" in basicInfo) || !basicInfo.ctas
      ? basicInfo
      : undefined;

  const t = useTranslations("COMMON");

  const singleDynamicCta = singleCtaBasicInfo?.callToActionText
    ? dynamicCtaButtonFactory(singleCtaBasicInfo, t)
    : null;

  const multipleDynamicCtas = listOfCtas?.length
    ? dynamicCtaButtonListFactory(listOfCtas, t)
    : null;

  const hasModuleData = !!moduleRankingPosition || !!moduleParentContainerType || !!moduleRankingPosition;

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      data-testid={testId}
      {...(hasModuleData && {
        "data-module-name": moduleName || "Text Module",
        "data-parent-container-type": moduleParentContainerType,
        "data-module-ranking-position": moduleRankingPosition,
        "data-ranking-algorithm": "manual",
        "data-track": "visibility",
        component: "section"
      })}
      sx={deepmerge(
        {
          padding: { xs: "4rem 2.4rem", md: "4rem 10rem", lg: "4rem 10rem 2rem 10rem" }
        },
        {
          ...slotProps?.root?.sx
        }
      )}
      className={slotProps?.root?.className || ""}
    >
      <Box
        sx={deepmerge(
          {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "2.4rem",
            position: "relative",
            zIndex: 2,
            maxWidth: "75rem"
          },
          {
            ...slotProps?.container?.sx
          }
        )}
        className={slotProps?.container?.className || ""}
      >
        {!hideText && (
          <SectionHeader
            headerText={headerText}
            copyText={copyText}
            headerTestId={slotProps?.header?.testId || "text-module-header"}
            copyTestId={slotProps?.copy?.testId || "text-module-copy"}
            headerSx={deepmerge(
              {
                textAlign: "center",
                textTransform: "none",
                color: "text.primary"
              },
              {
                ...slotProps?.header?.sx
              }
            )}
            copySx={deepmerge(
              {
                textAlign: "center",
                "& p": { margin: 0 },
                "& a": {
                  color: "secondary.main",
                  textDecoration: "underline !important"
                },
                fontSize: 18,
                color: "text.primary"
              },
              {
                ...slotProps?.copy?.sx
              }
            )}
          />
        )}

        {/* Single CTA */}
        {singleDynamicCta && (
          <DynamicCtaButton
            label={singleDynamicCta.label}
            ctaType={singleDynamicCta.ctaType}
            ctaData={singleDynamicCta.ctaData}
            testId={slotProps?.ctaButton?.testId || "text-module-cta"}
            sx={slotProps?.ctaButton?.sx}
            variant={singleDynamicCta.variant ?? "contained"}
            color={singleDynamicCta.color ?? "primary"}
          />
        )}
        {/* Multiple CTAs */}
        {multipleDynamicCtas && multipleDynamicCtas.length > 0 && (
          <Box
            sx={deepmerge(
              {},
              {
                ...slotProps?.ctaContainer?.sx
              }
            )}
            className={slotProps?.ctaContainer?.className || ""}
          >
            {multipleDynamicCtas.map((cta, index) => {
              const variant = index % 2 === 0 ? "outlined" : "contained";
              return (
                <DynamicCtaButton
                  key={cta.label || index}
                  label={cta.label}
                  ctaType={cta.ctaType}
                  ctaData={cta.ctaData}
                  testId={slotProps?.ctaButton?.testId || `text-module-cta-${index}`}
                  sx={slotProps?.ctaButton?.sx}
                  variant={cta.variant ?? variant}
                  color={cta.color ?? "primary"}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default TextModule;
